'use strict'

import React, { useMemo, useCallback } from 'react'
import CustomTooltip from '../../../../_components/CustomTooltip'
import { useModal } from '../../../../_components/Modal'
import { useModifyClassList } from '../../../../common/utils/CSSUtils'
import InfoIcon from '@mui/icons-material/Info'
import { useConversionTableSelector, useConversionTableDispatch } from '../extra/store'
import { DeleteButton } from '../../../common/CustomButton'
import { getTableKey, getTableName, getTableCount, getTableTarget, getTableMemo } from '../extra/helpers'

/**
 * 変換表サイドバーコンポーネント
 *
 * @param {Object} props
 * @param {function(Object): void} props.onSelectTable - 変換表選択ハンドラー
 * @param {function(): void} props.onNewTable - 新規作成ハンドラー
 * @param {function(Object): void} props.onEditTable - 編集ハンドラー
 * @param {function(Object): void} props.onDeleteTable - 削除ハンドラー
 * @returns {JSX.Element}
 */
const ConversionTableSidebar = ({ onSelectTable, onNewTable, onEditTable, onDeleteTable }) => {
  const showModal = useModal()
  const dispatch = useConversionTableDispatch()
  const tableList = useConversionTableSelector((s) => s.tableList)
  const selectedTable = useConversionTableSelector((s) => s.selectedTable)
  const sidebarSearch = useConversionTableSelector((s) => s.sidebarSearch)
  const classifications = useConversionTableSelector((s) => s.classifications)
  /** 区分マスター設定（ユーザー設定）の TypeFilter.itemErrorGetter と同様、変換項目バリデーションエラーで左タブを赤くする */
  const validationErrorByTableKey = useConversionTableSelector((s) => s.validationErrorByTableKey || {})

  const { promisify: promisifyModalWidth } = useModifyClassList('.modal-window', ['w-[600px]'])

  /** 区分マスター設定（ユーザー設定）の区分説明モーダルと同様の UI */
  const openTableMemoModal = useCallback(
    async (table) => {
      if (!showModal) return

      try {
        await promisifyModalWidth((res) => {
          showModal({
            title: getTableName(table) || '変換表の説明',
            isWide: false,
            isFooter: false,
            onClose: res,
            titleAlign: 'left',
            closeButtonStyle: 'icon',
            body: (
              <div className="py-4">
                <p className="text-base text-gray-700 whitespace-pre-wrap">
                  {getTableMemo(table) || '説明が設定されていません'}
                </p>
              </div>
            )
          })
        })
      } catch (error) {
        console.error('変換表説明モーダル表示エラー:', error)
      }
    },
    [showModal, promisifyModalWidth]
  )

  const filteredTableList = useMemo(() => {
    return sidebarSearch
      ? tableList.filter((t) => getTableName(t).toLowerCase().includes(sidebarSearch.toLowerCase()))
      : tableList
  }, [tableList, sidebarSearch])

  const getClassificationName = (key) => {
    if (!key) return ''
    const c = classifications?.find((x) => x.search_key === key)
    return c?.data_classification_name ?? key
  }

  return (
    <div
      className="card sidebar flex flex-col h-full min-w-0 overflow-x-hidden"
      style={{ padding: 12, minHeight: 0 }}
      data-testid="conversion-table-sidebar"
    >
      <div className="sidebar-header btn-group flex items-center justify-between mb-2">
        <span className="font-semibold">変換表</span>
        <button type="button" className="header-btn !p-0" onClick={onNewTable} data-testid="sidebar-new-register">
          <span className="header-btn white--text py-1.5 pl-7 pr-10 inline-flex items-center">
            <span className="icon-plus-line icon-32px mr-1.5 my-px" />
            <span className="-translate-y-px text-h7">新規登録</span>
          </span>
        </button>
      </div>
      <input
        type="text"
        className="input mb-2 p-2 border border-gray-200 rounded-lg min-w-0 w-full max-w-full"
        placeholder="変換表を検索…"
        value={sidebarSearch}
        onChange={(e) => dispatch('SET_SIDEBAR_SEARCH', e.target.value)}
      />
      <div
        className="list flex flex-col gap-1 flex-1 overflow-y-auto pr-1"
        style={{ minHeight: 0 }}
        data-testid="conversion-table-list"
      >
        {filteredTableList.length === 0 ? (
          <p className="text-caption-2 grey--text py-4 text-center">変換表がありません</p>
        ) : (
          <div className="flex flex-col gap-1 min-w-0">
            {filteredTableList.map((table, index) => {
              const key = getTableKey(table)
              const isSelected = selectedTable && getTableKey(selectedTable) === key
              const hasValidationError = validationErrorByTableKey[key] === true
              const rowSurface = hasValidationError
                ? isSelected
                  ? 'bg-red-200 hover:bg-red-200/90 ring-2 ring-inset ring-red-300/80'
                  : 'bg-red-100 hover:bg-red-200/90'
                : isSelected
                  ? 'bg-blue-50 border border-blue-600'
                  : 'border border-transparent'
              return (
                <div
                  key={key + '_' + index}
                  role="button"
                  tabIndex={0}
                  className={`item flex flex-col p-2 rounded-lg cursor-pointer min-w-0 max-w-full overflow-hidden ${rowSurface}`}
                  data-testid="sidebar-table-item"
                  data-table-name={getTableName(table)}
                  data-table-key={key}
                  onClick={() => onSelectTable(table)}
                  onKeyDown={(e) => e.key === 'Enter' && onSelectTable(table)}
                >
                  <div className="top-row flex items-center justify-between gap-1 min-w-0">
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <CustomTooltip title={getTableName(table)} placement="top">
                        {(ref) => (
                          <div ref={ref} className="name font-semibold block w-full min-w-0 truncate">
                            {getTableName(table)}
                          </div>
                        )}
                      </CustomTooltip>
                    </div>
                    <CustomTooltip title="変換表の説明を表示">
                      {(ref) => (
                        <button
                          ref={ref}
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            openTableMemoModal(table)
                          }}
                          className="flex items-center justify-center p-0.5 hover:bg-gray-200 rounded transition-colors"
                          aria-label="変換表の説明を表示"
                        >
                          <InfoIcon sx={{ color: '#297AD8' }} fontSize="14px" />
                        </button>
                      )}
                    </CustomTooltip>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="edit-btn bg-transparent border-none cursor-pointer p-0.5"
                        onClick={(e) => {
                          e.stopPropagation()
                          onEditTable(table)
                        }}
                        aria-label="編集"
                        data-testid="sidebar-edit-table"
                      >
                        <span>✎</span>
                      </button>
                      <DeleteButton
                        className="flex"
                        data-testid="sidebar-table-delete"
                        onDelete={(e) => {
                          e.stopPropagation()
                          onDeleteTable && onDeleteTable(table)
                        }}
                        disabled={false}
                      />
                    </div>
                  </div>
                  <div className="info-row flex items-center gap-1.5 flex-1 min-w-0">
                    <span className="count text-xs grey--text">
                      {getTableCount(table) > 0 ? `${getTableCount(table)}件` : '未登録'}
                    </span>
                    {getTableTarget(table) && (
                      <span className="category-tag text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                        {getClassificationName(getTableTarget(table))}
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default ConversionTableSidebar
